import React from 'react';

interface DevConfig {
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  enablePerformanceTracking: boolean;
  mockApiDelay: number;
  showBoundingBoxes: boolean;
  highlightReRenders: boolean;
}

class DevUtils {
  private config: DevConfig = {
    logLevel: 'debug',
    enablePerformanceTracking: true,
    mockApiDelay: 500,
    showBoundingBoxes: false,
    highlightReRenders: false
  };

  private isDevelopment = process.env.NODE_ENV === 'development';

  // Enhanced logging with context
  log = {
    debug: (message: string, ...args: any[]) => {
      if (!this.isDevelopment || this.config.logLevel === 'error') return;
      console.log(`🐛 [DEBUG] ${message}`, ...args);
    },

    info: (message: string, ...args: any[]) => {
      if (!this.isDevelopment || ['error', 'warn'].includes(this.config.logLevel)) return;
      console.info(`ℹ️ [INFO] ${message}`, ...args);
    },

    warn: (message: string, ...args: any[]) => {
      if (!this.isDevelopment || this.config.logLevel === 'error') return;
      console.warn(`⚠️ [WARN] ${message}`, ...args);
    },

    error: (message: string, ...args: any[]) => {
      if (!this.isDevelopment) return;
      console.error(`❌ [ERROR] ${message}`, ...args);
    },

    group: (label: string, callback: () => void) => {
      if (!this.isDevelopment) return;
      console.group(`📁 ${label}`);
      callback();
      console.groupEnd();
    },

    time: (label: string) => {
      if (!this.isDevelopment) return;
      console.time(`⏱️ ${label}`);
    },

    timeEnd: (label: string) => {
      if (!this.isDevelopment) return;
      console.timeEnd(`⏱️ ${label}`);
    }
  };

  // Performance tracking
  perf = {
    mark: (name: string) => {
      if (!this.isDevelopment || !this.config.enablePerformanceTracking) return;
      performance.mark(name);
    },

    measure: (name: string, startMark: string, endMark?: string) => {
      if (!this.isDevelopment || !this.config.enablePerformanceTracking) return;
      try {
        performance.measure(name, startMark, endMark);
        const measurement = performance.getEntriesByName(name, 'measure')[0];
        this.log.debug(`Performance: ${name}`, `${measurement.duration.toFixed(2)}ms`);
        return measurement.duration;
      } catch (error) {
        this.log.warn(`Failed to measure performance: ${name}`, error);
      }
    },

    trackComponentRender: (componentName: string) => {
      if (!this.isDevelopment) return () => {};
      
      const startTime = performance.now();
      return () => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (duration > 16) { // Slower than 60fps
          this.log.warn(`Slow render: ${componentName}`, `${duration.toFixed(2)}ms`);
        } else {
          this.log.debug(`Render: ${componentName}`, `${duration.toFixed(2)}ms`);
        }
      };
    },

    trackAsyncOperation: async <T>(
      operationName: string, 
      operation: () => Promise<T>
    ): Promise<T> => {
      if (!this.isDevelopment) return operation();
      
      const startTime = performance.now();
      this.log.debug(`Starting async operation: ${operationName}`);
      
      try {
        const result = await operation();
        const duration = performance.now() - startTime;
        this.log.debug(`Completed async operation: ${operationName}`, `${duration.toFixed(2)}ms`);
        return result;
      } catch (error) {
        const duration = performance.now() - startTime;
        this.log.error(`Failed async operation: ${operationName}`, `${duration.toFixed(2)}ms`, error);
        throw error;
      }
    }
  };

  // Mock data utilities
  mock = {
    delay: (customDelay?: number) => {
      if (!this.isDevelopment) return Promise.resolve();
      const delay = customDelay ?? this.config.mockApiDelay;
      return new Promise(resolve => setTimeout(resolve, delay));
    },

    generateId: () => `mock_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,

    generateEvent: (overrides: any = {}) => ({
      id: this.mock.generateId(),
      title: `Sample Event ${Math.floor(Math.random() * 100)}`,
      description: 'This is a mock event for development purposes.',
      start_date: new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      end_date: new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000).toISOString(),
      location: `Mock Venue ${Math.floor(Math.random() * 10)}`,
      price: Math.floor(Math.random() * 100),
      total_tickets: Math.floor(Math.random() * 500) + 50,
      available_tickets: Math.floor(Math.random() * 300) + 10,
      image_url: `https://picsum.photos/400/300?random=${Math.floor(Math.random() * 1000)}`,
      is_published: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      organizer_id: this.mock.generateId(),
      category_id: this.mock.generateId(),
      ...overrides
    }),

    generateUser: (overrides: any = {}) => ({
      id: this.mock.generateId(),
      email: `mock.user${Math.floor(Math.random() * 1000)}@example.com`,
      display_name: `Mock User ${Math.floor(Math.random() * 100)}`,
      avatar_url: `https://picsum.photos/100/100?random=${Math.floor(Math.random() * 1000)}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ...overrides
    }),

    apiResponse: <T>(data: T, delay?: number) => {
      return new Promise<{ data: T; error: null }>((resolve) => {
        setTimeout(() => {
          resolve({ data, error: null });
        }, delay ?? this.config.mockApiDelay);
      });
    },

    apiError: (message: string, delay?: number) => {
      return new Promise<{ data: null; error: { message: string } }>((resolve) => {
        setTimeout(() => {
          resolve({ data: null, error: { message } });
        }, delay ?? this.config.mockApiDelay);
      });
    }
  };

  // Component debugging utilities
  component = {
    logProps: <P extends object>(componentName: string, props: P) => {
      if (!this.isDevelopment) return;
      this.log.group(`Props for ${componentName}`, () => {
        console.table(props);
      });
    },

    logState: <S extends object>(componentName: string, state: S) => {
      if (!this.isDevelopment) return;
      this.log.group(`State for ${componentName}`, () => {
        console.table(state);
      });
    },

    highlightReRender: (ref: React.RefObject<HTMLElement>) => {
      if (!this.isDevelopment || !this.config.highlightReRenders || !ref.current) return;
      
      const element = ref.current;
      element.style.outline = '2px solid red';
      element.style.backgroundColor = 'rgba(255, 0, 0, 0.1)';
      
      setTimeout(() => {
        element.style.outline = '';
        element.style.backgroundColor = '';
      }, 1000);
    },

    showBoundingBox: (ref: React.RefObject<HTMLElement>) => {
      if (!this.isDevelopment || !this.config.showBoundingBoxes || !ref.current) return;
      
      const element = ref.current;
      element.style.border = '1px dashed blue';
    }
  };

  // Error simulation
  error = {
    simulateNetworkError: () => {
      if (!this.isDevelopment) return false;
      return Math.random() < 0.1; // 10% chance
    },

    simulateSlowNetwork: () => {
      if (!this.isDevelopment) return 0;
      return Math.random() < 0.2 ? 3000 : 0; // 20% chance of 3s delay
    },

    simulateApiError: (errorRate: number = 0.1) => {
      if (!this.isDevelopment) return false;
      return Math.random() < errorRate;
    }
  };

  // Configuration
  configure = (newConfig: Partial<DevConfig>) => {
    if (!this.isDevelopment) return;
    this.config = { ...this.config, ...newConfig };
    this.log.info('DevUtils configured', this.config);
  };

  getConfig = () => ({ ...this.config });

  // Feature flags for development
  featureFlags = {
    isEnabled: (flagName: string): boolean => {
      if (!this.isDevelopment) return false;
      const flags = JSON.parse(localStorage.getItem('dev_feature_flags') || '{}');
      return flags[flagName] === true;
    },

    enable: (flagName: string) => {
      if (!this.isDevelopment) return;
      const flags = JSON.parse(localStorage.getItem('dev_feature_flags') || '{}');
      flags[flagName] = true;
      localStorage.setItem('dev_feature_flags', JSON.stringify(flags));
      this.log.info(`Feature flag enabled: ${flagName}`);
    },

    disable: (flagName: string) => {
      if (!this.isDevelopment) return;
      const flags = JSON.parse(localStorage.getItem('dev_feature_flags') || '{}');
      flags[flagName] = false;
      localStorage.setItem('dev_feature_flags', JSON.stringify(flags));
      this.log.info(`Feature flag disabled: ${flagName}`);
    },

    list: () => {
      if (!this.isDevelopment) return {};
      return JSON.parse(localStorage.getItem('dev_feature_flags') || '{}');
    }
  };

  // Global debug helpers (available in console)
  exposeGlobals = () => {
    if (!this.isDevelopment || typeof window === 'undefined') return;
    
    (window as any).__DEV__ = this;
    this.log.info('DevUtils exposed as window.__DEV__');
  };
}

// Create singleton instance
export const devUtils = new DevUtils();

// Expose globally in development
if (process.env.NODE_ENV === 'development') {
  devUtils.exposeGlobals();
}

// React hook for component performance tracking
export const useDevPerformance = (componentName: string) => {
  if (process.env.NODE_ENV !== 'development') {
    return { trackRender: () => {} };
  }

  const trackRender = devUtils.perf.trackComponentRender(componentName);

  React.useEffect(() => {
    return trackRender;
  });

  return { trackRender };
};

// React hook for development logs
export const useDevLog = (componentName: string) => {
  if (process.env.NODE_ENV !== 'development') {
    return { log: () => {}, logProps: () => {}, logState: () => {} };
  }

  const log = (message: string, ...args: any[]) => {
    devUtils.log.debug(`[${componentName}] ${message}`, ...args);
  };

  const logProps = (props: any) => {
    devUtils.component.logProps(componentName, props);
  };

  const logState = (state: any) => {
    devUtils.component.logState(componentName, state);
  };

  return { log, logProps, logState };
};

export default devUtils;